/**
 * Landing Page V3 JavaScript
 * Handles animations and interactions for TEQ LITE V3 landing pages
 */

(function () {
  "use strict";

  // Initialize when DOM is ready
  function init() {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", function () {
        initV3Animations();
        initSmoothScroll();
      });
    } else {
      initV3Animations();
      initSmoothScroll();
    }

    initResizeHandler();

    if (typeof ScrollToPlugin !== "undefined") {
      gsap.registerPlugin(ScrollToPlugin);
    }
  }

  function initV3Animations() {
    initHeroVideoAnimation();
    initRevealAnimations();
    initFaqAccordion();
  }

  // Store hero ScrollTrigger instance for cleanup on resize
  let heroVideoTrigger = null;

  /**
   * Hero Video Scroll Animation
   */
  function initHeroVideoAnimation() {
    const videoSection = document.querySelector(".v3-hero__video-section");
    const videoWrapper = document.querySelector(".v3-hero__video-wrapper");

    if (!videoSection || !videoWrapper) return;

    if (heroVideoTrigger) {
      heroVideoTrigger.kill();
      heroVideoTrigger = null;
      gsap.set(videoWrapper, { clearProps: "all" });
    }

    const isMobile = window.innerWidth < 768;
    const startScale = isMobile ? 0.6 : 0.5;
    const startOpacity = 0.4;

    gsap.set(videoWrapper, {
      scale: startScale,
      opacity: startOpacity,
    });

    const pinDuration = isMobile ? 600 : 1000;
    const scaleInEnd = 0.2;
    const scaleOutStart = 0.8;

    heroVideoTrigger = ScrollTrigger.create({
      trigger: videoSection,
      start: "center center",
      end: "+=" + pinDuration,
      pin: true,
      pinSpacing: true,
      scrub: 1,
      onUpdate: (self) => {
        const progress = self.progress;
        let currentScale, currentOpacity;

        if (progress <= scaleInEnd) {
          const animationProgress = progress / scaleInEnd;
          currentScale = startScale + (1 - startScale) * animationProgress;
          currentOpacity =
            startOpacity + (1 - startOpacity) * animationProgress;
        } else if (progress >= scaleOutStart) {
          const animationProgress =
            (progress - scaleOutStart) / (1 - scaleOutStart);
          currentScale = 1 - (1 - startScale) * animationProgress;
          currentOpacity = 1 - (1 - startOpacity) * animationProgress;
        } else {
          currentScale = 1;
          currentOpacity = 1;
        }

        gsap.set(videoWrapper, {
          scale: currentScale,
          opacity: currentOpacity,
        });
      },
    });
  }

  /**
   * Reveal Animations for sections
   */
  function initRevealAnimations() {
    const revealElements = document.querySelectorAll(".v3-reveal");
    if (revealElements.length === 0) return;

    const staggerContainers = [
      { selector: ".v3-features__grid", itemSelector: ".v3-reveal" },
      { selector: ".v3-testimonials__grid", itemSelector: ".v3-testimonial" },
      { selector: ".v3-policy__grid", itemSelector: ".v3-policy__card" },
      { selector: ".v3-benefits__list", itemSelector: ".v3-benefits__item" },
    ];

    const staggeredElements = new Set();

    staggerContainers.forEach(({ selector, itemSelector }) => {
      const container = document.querySelector(selector);
      if (!container) return;

      const items = container.querySelectorAll(itemSelector);
      if (items.length === 0) return;

      items.forEach((item) => staggeredElements.add(item));

      gsap.set(items, { opacity: 0, y: 30 });

      ScrollTrigger.create({
        trigger: container,
        start: "top 80%",
        onEnter: () => {
          gsap.to(items, {
            opacity: 1,
            y: 0,
            duration: 0.6,
            stagger: 0.1,
            ease: "power2.out",
          });
        },
        once: true,
      });
    });

    revealElements.forEach((element) => {
      if (staggeredElements.has(element)) return;

      gsap.set(element, { opacity: 0, y: 30 });

      ScrollTrigger.create({
        trigger: element,
        start: "top 85%",
        onEnter: () => {
          gsap.to(element, {
            opacity: 1,
            y: 0,
            duration: 0.6,
            ease: "power2.out",
          });
        },
        once: true,
      });
    });
  }

  /**
   * FAQ Accordion
   */
  function initFaqAccordion() {
    const faqItems = document.querySelectorAll(".v3-faq__item");
    if (faqItems.length === 0) return;

    faqItems.forEach((item) => {
      const question = item.querySelector(".v3-faq__question");
      const answer = item.querySelector(".v3-faq__answer");

      if (!question || !answer) return;

      gsap.set(answer, { height: 0, opacity: 0 });

      question.addEventListener("click", () => {
        const isOpen = item.classList.contains("is-open");

        faqItems.forEach((otherItem) => {
          if (otherItem !== item && otherItem.classList.contains("is-open")) {
            otherItem.classList.remove("is-open");
            const otherAnswer = otherItem.querySelector(".v3-faq__answer");
            gsap.to(otherAnswer, {
              height: 0,
              opacity: 0,
              duration: 0.3,
              ease: "power2.inOut",
            });
          }
        });

        if (isOpen) {
          item.classList.remove("is-open");
          gsap.to(answer, {
            height: 0,
            opacity: 0,
            duration: 0.3,
            ease: "power2.inOut",
          });
        } else {
          item.classList.add("is-open");
          gsap.to(answer, {
            height: "auto",
            opacity: 1,
            duration: 0.3,
            ease: "power2.inOut",
          });
        }
      });
    });

    if (faqItems.length > 0) {
      const firstItem = faqItems[0];
      const firstAnswer = firstItem.querySelector(".v3-faq__answer");
      firstItem.classList.add("is-open");
      gsap.set(firstAnswer, { height: "auto", opacity: 1 });
    }
  }

  /**
   * Smooth scroll for anchor links
   */
  function initSmoothScroll() {
    const anchors = document.querySelectorAll('a[href^="#"], [data-scroll-to]');

    anchors.forEach((anchor) => {
      anchor.addEventListener("click", function (e) {
        const targetId =
          this.getAttribute("data-scroll-to") || this.getAttribute("href");

        if (!targetId || targetId === "#") return;

        let targetElement = document.querySelector(targetId);

        // Special case: #how-it-works scrolls to first section after hero
        if (!targetElement && targetId === "#how-it-works") {
          const heroSection = document.querySelector(".v3-hero");
          if (heroSection) {
            const heroShopifySection = heroSection.closest(
              '[id^="shopify-section-"]',
            );
            if (heroShopifySection && heroShopifySection.nextElementSibling) {
              targetElement = heroShopifySection.nextElementSibling;
            }
          }
        }

        if (targetElement) {
          e.preventDefault();

          if (
            typeof gsap !== "undefined" &&
            typeof ScrollToPlugin !== "undefined"
          ) {
            gsap.to(window, {
              duration: 1,
              scrollTo: {
                y: targetElement,
                offsetY: 100,
              },
              ease: "power2.inOut",
            });
          } else {
            // Fallback to native smooth scroll
            const offsetTop =
              targetElement.getBoundingClientRect().top +
              window.pageYOffset -
              100;
            window.scrollTo({
              top: offsetTop,
              behavior: "smooth",
            });
          }
        }
      });
    });
  }

  /**
   * Handle window resize
   */
  function initResizeHandler() {
    let resizeTimeout;
    window.addEventListener("resize", function () {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(function () {
        initHeroVideoAnimation();
      }, 250);
    });
  }

  // Start initialization
  init();
})();
